import { Injectable, inject, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'angular-oauth2-oidc';
import { local, development, uat, staging, production } from '../core/config/env.file';

@Injectable({
  providedIn: 'root'
})
export class SessionMonitorService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly oauthService = inject(OAuthService);
  
  private sessionCheckInterval: ReturnType<typeof setInterval> | null = null;
  private readonly CHECK_INTERVAL = 30000; // Check every 30 seconds
  private readonly TOKEN_BUFFER = 15000; // 15 seconds buffer before token expires

  constructor() {
    // Don't start automatically - let app component start it after login processing
    // This prevents premature session checks during initial login flow
  }

  ngOnDestroy(): void {
    this.stopSessionMonitoring();
  }

  /**
   * Start monitoring user session for expiration
   */
  private startSessionMonitoring(): void {
    // Don't start if already running
    if (this.sessionCheckInterval) {
      return;
    }

    this.sessionCheckInterval = setInterval(() => {
      this.checkSessionValidity();
    }, this.CHECK_INTERVAL);
  }

  /**
   * Public method to start session monitoring (useful after login)
   */
  public startMonitoring(): void {
    this.startSessionMonitoring();
  }

  /**
   * Stop session monitoring
   */
  private stopSessionMonitoring(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
      this.sessionCheckInterval = null;
    }
  }

  /**
   * Check if current session is still valid
   */
  private checkSessionValidity(): void {
    // Check if we're on a login page or public page - skip monitoring
    if (this.isOnPublicPage()) {
      return;
    }

    // Check if this is during initial login flow (tokens being processed)
    if (this.isInitialLoginFlow()) {
      return;
    }

    // Check OAuth service first
    if (this.oauthService.hasValidAccessToken()) {
      return; // Session is still valid
    }

    // Fallback: Check localStorage token manually
    const accessToken = localStorage.getItem('access_token');
    if (accessToken && this.isTokenValid(accessToken)) {
      return; // Session is still valid
    }

    // Session has expired - redirect to login
    console.warn('Session expired - redirecting to login');
    this.redirectToLogin();
  }

   /**
   * Check if we're in the initial login flow where tokens are being processed
   */
  private isInitialLoginFlow(): boolean {
    const currentUrl = window.location.href;
    
    // Check if URL contains login parameters (indicating fresh login)
    const hasLoginParams = currentUrl.includes('access_token=') || 
                          currentUrl.includes('user_roles=') ||
                          currentUrl.includes('sessionCorrelationId=');
    
    // Also check if tokens were recently set (within last 10 seconds)
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      try {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const tokenIssuedAt = payload.iat * 1000; // Convert to milliseconds
        const now = Date.now();
        const recentlyIssued = (now - tokenIssuedAt) < 10000; // Within last 10 seconds
        
        if (recentlyIssued) {
          return true;
        }
      } catch (error) {
        // Token parsing failed - treat as not recently issued
        console.debug('Token parsing failed during initial login flow check:', error);
      }
    }
    
    return hasLoginParams;
  }  /**
   * Check if current page is a public page where session monitoring is not needed
   */
  private isOnPublicPage(): boolean {
    const currentUrl = window.location.href;
    const publicPages = ['/auth/login', '/auth/logout', '/auth/nafath'];
    
    return publicPages.some(page => currentUrl.includes(page));
  }

  /**
   * Validate token expiration with buffer time
   */
  private isTokenValid(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      return exp > (now + this.TOKEN_BUFFER);
    } catch (error) {
      console.debug('Token validation failed:', error);
      return false;
    }
  }

  /**
   * Redirect user to login page and clear tokens
   */
  private redirectToLogin(): void {
    // Stop monitoring to prevent multiple redirects
    this.stopSessionMonitoring();
    
    // Clear tokens
    this.clearAuthTokens();
    
    // Determine correct login URL based on environment
    const loginUrl = this.getLoginUrl();
    
    // Redirect to login
    window.location.replace(loginUrl);

  }

  /**
   * Clear all authentication tokens
   */
  private clearAuthTokens(): void {
    // Clear OAuth service tokens
    this.oauthService.logOut();
    
    // Clear localStorage tokens as fallback
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_roles');
    localStorage.removeItem('sessionCorrelationId');
    localStorage.removeItem('expires_in');
  }

  /**
   * Get appropriate login URL based on current environment
   */
  private getLoginUrl(): string {
    const currentHost = window.location.href;
    
    if (currentHost.includes('.dev')) {
      return `${development.publicSite}/auth/logout`;
    } else if (currentHost.includes('.qa')) {
      return `${staging.publicSite}/auth/logout`;
    } else if (currentHost.includes('.uat')) {
      return `${uat.publicSite}/auth/logout`;
    } else if (currentHost.includes('.infrareg.sa')) {
      return `${production.publicSite}/auth/logout`;
    } 
    else {
      return `${local.publicSite}/auth/logout`;
    }
  }

  /**
   * Force immediate session check (useful for logout scenarios)
   */
  public forceSessionCheck(): void {
    this.checkSessionValidity();
  }

  /**
   * Manually trigger logout and redirect
   */
  public logout(): void {
    this.redirectToLogin();
  }
}